import React, { useState, useContext } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Link,
  Alert,
  ButtonGroup
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as ApproveIcon,
  Block as RejectIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { 
  getSourceLabel, 
  getPaymentStatusLabel, 
  getDeliveryStatusLabel, 
  getDepositStatusLabel,
  getPaymentStatusColor,
  getDeliveryStatusColor,
  getDepositStatusColor,
  PAYMENT_STATUSES,
  DELIVERY_STATUSES,
  DEPOSIT_STATUSES
} from '../../constants/salesConstants';

const SaleDetails = ({ sale, onUpdate, onClose }) => {
  const { isAdmin, currentUser } = useContext(AuthContext);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState({ text: '', type: 'info' });
  
  // Edytowalne pola
  const [editedSale, setEditedSale] = useState({
    paymentStatus: sale.paymentStatus || 'pending_payment',
    deliveryStatus: sale.deliveryStatus || 'pending_delivery',
    notes: sale.notes || '',
    depositStatus: sale.deposit?.status || 'pending'
  });

  const handleEditToggle = () => {
    if (editing) {
      // Anuluj edycję
      setEditedSale({
        paymentStatus: sale.paymentStatus || 'pending_payment',
        deliveryStatus: sale.deliveryStatus || 'pending_delivery',
        notes: sale.notes || '',
        depositStatus: sale.deposit?.status || 'pending'
      });
    }
    setEditing(!editing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedSale({
      ...editedSale,
      [name]: value
    });
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      
      const saleRef = doc(db, 'sales', sale.id);
      const updateData = {
        paymentStatus: editedSale.paymentStatus,
        deliveryStatus: editedSale.deliveryStatus,
        notes: editedSale.notes,
        lastUpdate: new Date()
      };
      
      // Aktualizacja statusu zadatku, jeśli istnieje
      if (sale.deposit) {
        updateData['deposit.status'] = editedSale.depositStatus;
      }
      
      await updateDoc(saleRef, updateData);
      
      // Powiadom rodzica o aktualizacji
      if (onUpdate) {
        onUpdate({
          ...sale,
          ...updateData,
          deposit: sale.deposit ? {
            ...sale.deposit,
            status: editedSale.depositStatus
          } : null
        });
      }
      
      setEditing(false);
      setLoading(false);
    } catch (error) {
      console.error('Błąd podczas aktualizacji sprzedaży:', error);
      setLoading(false);
    }
  };

  // Funkcja do zatwierdzania sprzedaży przez admina
  const handleApproveSale = async () => {
    if (!isAdmin) return;
    
    try {
      setActionLoading(true);
      setActionMessage({ text: 'Zatwierdzanie sprzedaży...', type: 'info' });
      
      // 1. Aktualizuj status sprzedaży
      const saleRef = doc(db, 'sales', sale.id);
      await updateDoc(saleRef, {
        paymentStatus: 'completed',
        deliveryStatus: 'completed',
        status: 'approved',
        approvedBy: currentUser.uid,
        approvedAt: new Date(),
        lastUpdate: new Date()
      });
      
      // 2. Jeśli istnieje powiązany produkt, usuń go z bazy danych
      if (sale.productId) {
        const productRef = doc(db, 'products', sale.productId);
        const productSnap = await getDoc(productRef);
        
        if (productSnap.exists()) {
          // Usuń produkt
          await deleteDoc(productRef);
          console.log(`Produkt ${sale.productId} został usunięty po zatwierdzeniu sprzedaży`);
        }
      }
      
      // 3. Zaktualizuj również raport sprzedaży, jeśli istnieje
      if (sale.sale_report_id) {
        const reportRef = doc(db, 'sales_reports', sale.sale_report_id);
        await updateDoc(reportRef, {
          status: 'approved',
          approvedBy: currentUser.uid,
          approvedAt: new Date(),
          lastUpdate: new Date()
        });
      }
      
      setActionMessage({ text: 'Sprzedaż została zatwierdzona pomyślnie', type: 'success' });
      
      // Powiadom rodzica o aktualizacji
      if (onUpdate) {
        onUpdate({
          ...sale,
          paymentStatus: 'completed',
          deliveryStatus: 'completed',
          status: 'approved',
          approvedBy: currentUser.uid,
          approvedAt: new Date(),
          lastUpdate: new Date()
        });
      }
      
      // Zamknij dialog po 2 sekundach
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Błąd podczas zatwierdzania sprzedaży:', error);
      setActionMessage({ text: `Błąd: ${error.message}`, type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Funkcja do odrzucania sprzedaży przez admina
  const handleRejectSale = async () => {
    if (!isAdmin) return;
    
    try {
      setActionLoading(true);
      setActionMessage({ text: 'Odrzucanie sprzedaży...', type: 'info' });
      
      // 1. Aktualizuj status sprzedaży
      const saleRef = doc(db, 'sales', sale.id);
      await updateDoc(saleRef, {
        status: 'rejected',
        rejectedBy: currentUser.uid,
        rejectedAt: new Date(),
        lastUpdate: new Date()
      });
      
      // 2. Jeśli istnieje powiązany produkt, przywróć jego status na 'available'
      if (sale.productId) {
        const productRef = doc(db, 'products', sale.productId);
        const productSnap = await getDoc(productRef);
        
        if (productSnap.exists()) {
          // Przywróć status produktu na 'available'
          await updateDoc(productRef, {
            status: 'available',
            updated_at: new Date()
          });
          console.log(`Status produktu ${sale.productId} został przywrócony na 'available'`);
        }
      }
      
      // 3. Zaktualizuj również raport sprzedaży, jeśli istnieje
      if (sale.sale_report_id) {
        const reportRef = doc(db, 'sales_reports', sale.sale_report_id);
        await updateDoc(reportRef, {
          status: 'rejected',
          rejectedBy: currentUser.uid,
          rejectedAt: new Date(),
          lastUpdate: new Date()
        });
      }
      
      setActionMessage({ text: 'Sprzedaż została odrzucona', type: 'warning' });
      
      // Powiadom rodzica o aktualizacji
      if (onUpdate) {
        onUpdate({
          ...sale,
          status: 'rejected',
          rejectedBy: currentUser.uid,
          rejectedAt: new Date(),
          lastUpdate: new Date()
        });
      }
      
      // Zamknij dialog po 2 sekundach
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Błąd podczas odrzucania sprzedaży:', error);
      setActionMessage({ text: `Błąd: ${error.message}`, type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Brak daty';
    return format(date instanceof Date ? date : date.toDate(), 'dd.MM.yyyy HH:mm', { locale: pl });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Sekcja 1: Dane podstawowe sprzedaży */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Szczegóły sprzedaży</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Przyciski zatwierdzania/odrzucania dla admina */}
            {isAdmin && sale.status === 'pending' && !editing && (
              <ButtonGroup variant="contained">
                <Button
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={handleApproveSale}
                  disabled={actionLoading}
                >
                  Zatwierdź
                </Button>
                <Button
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={handleRejectSale}
                  disabled={actionLoading}
                >
                  Odrzuć
                </Button>
              </ButtonGroup>
            )}
            
            {/* Przycisk edycji statusów */}
            {(isAdmin || sale.source === 'order') && (
              <Button
                variant={editing ? "outlined" : "contained"}
                color={editing ? "secondary" : "primary"}
                startIcon={editing ? <CancelIcon /> : <EditIcon />}
                onClick={handleEditToggle}
                disabled={loading || actionLoading}
              >
                {editing ? "Anuluj edycję" : "Edytuj statusy"}
              </Button>
            )}
          </Box>
        </Box>
        
        {/* Komunikat o statusie akcji */}
        {actionMessage.text && (
          <Alert severity={actionMessage.type} sx={{ mb: 2 }}>
            {actionMessage.text}
          </Alert>
        )}
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Informacje podstawowe</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">ID sprzedaży</Typography>
                  <Typography variant="body1">{sale.id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Data transakcji</Typography>
                  <Typography variant="body1">{formatDate(sale.transactionDate)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Klient</Typography>
                  <Typography variant="body1">
                    <Link href={`/clients/${sale.clientId}`}>
                      {sale.clientName || sale.clientId}
                    </Link>
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Sprzedawca</Typography>
                  <Typography variant="body1">{sale.employeeName || sale.employeeId}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Źródło</Typography>
                  <Chip 
                    label={getSourceLabel(sale.source)} 
                    color={sale.source === 'order' ? 'secondary' : 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Wartość całkowita</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatPrice(sale.totalAmount || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Nr faktury</Typography>
                  <Typography variant="body1">{sale.invoiceNumber || 'Brak'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Statusy</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {editing ? (
                    <FormControl fullWidth margin="normal" size="small">
                      <InputLabel>Status płatności</InputLabel>
                      <Select
                        name="paymentStatus"
                        value={editedSale.paymentStatus}
                        onChange={handleInputChange}
                        label="Status płatności"
                      >
                        {Object.entries(PAYMENT_STATUSES).map(([key, value]) => (
                          <MenuItem key={key} value={key}>{value}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <>
                      <Typography variant="body2" color="textSecondary">Status płatności</Typography>
                      <Chip 
                        label={getPaymentStatusLabel(sale.paymentStatus)} 
                        color={getPaymentStatusColor(sale.paymentStatus)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </>
                  )}
                </Grid>
                <Grid item xs={12}>
                  {editing ? (
                    <FormControl fullWidth margin="normal" size="small">
                      <InputLabel>Status dostawy</InputLabel>
                      <Select
                        name="deliveryStatus"
                        value={editedSale.deliveryStatus}
                        onChange={handleInputChange}
                        label="Status dostawy"
                      >
                        {Object.entries(DELIVERY_STATUSES).map(([key, value]) => (
                          <MenuItem key={key} value={key}>{value}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <>
                      <Typography variant="body2" color="textSecondary">Status dostawy</Typography>
                      <Chip 
                        label={getDeliveryStatusLabel(sale.deliveryStatus)} 
                        color={getDeliveryStatusColor(sale.deliveryStatus)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </>
                  )}
                </Grid>
                <Grid item xs={12}>
                  {editing ? (
                    <TextField
                      fullWidth
                      label="Notatki"
                      name="notes"
                      value={editedSale.notes}
                      onChange={handleInputChange}
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      margin="normal"
                    />
                  ) : (
                    <>
                      <Typography variant="body2" color="textSecondary">Notatki</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {sale.notes || 'Brak notatek'}
                      </Typography>
                    </>
                  )}
                </Grid>
                {editing && (
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveChanges}
                      disabled={loading}
                    >
                      Zapisz zmiany
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Sekcja 2: Szczegóły produktów */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Produkty</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nazwa produktu</TableCell>
                <TableCell align="right">Ilość</TableCell>
                <TableCell align="right">Cena</TableCell>
                <TableCell align="right">Wartość</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sale.products && sale.products.map((product, index) => (
                <TableRow key={index}>
                  <TableCell>{product.name}</TableCell>
                  <TableCell align="right">{product.quantity}</TableCell>
                  <TableCell align="right">{formatPrice(product.price)}</TableCell>
                  <TableCell align="right">{formatPrice(product.price * product.quantity)}</TableCell>
                  <TableCell align="center">
                    {product.isReserved && (
                      <Chip
                        icon={<LockIcon />}
                        label="Zarezerwowany"
                        size="small"
                        color="secondary"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Sekcja 3: Szczegóły zadatku (jeśli istnieje) */}
      {sale.deposit && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Zadatek</Typography>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Kwota zadatku</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatPrice(sale.deposit.amount || 0)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Data wpłaty</Typography>
                <Typography variant="body1">
                  {formatDate(sale.deposit.paidAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                {editing ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Status zadatku</InputLabel>
                    <Select
                      name="depositStatus"
                      value={editedSale.depositStatus}
                      onChange={handleInputChange}
                      label="Status zadatku"
                    >
                      {Object.entries(DEPOSIT_STATUSES).map(([key, value]) => (
                        <MenuItem key={key} value={key}>{value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <>
                    <Typography variant="body2" color="textSecondary">Status zadatku</Typography>
                    <Chip 
                      label={getDepositStatusLabel(sale.deposit.status)} 
                      color={getDepositStatusColor(sale.deposit.status)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      {/* Sekcja 4: Informacje dodatkowe */}
      <Box>
        <Typography variant="h6" gutterBottom>Informacje dodatkowe</Typography>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">Data utworzenia</Typography>
              <Typography variant="body1">
                {formatDate(sale.createdAt)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">Ostatnia aktualizacja</Typography>
              <Typography variant="body1">
                {formatDate(sale.lastUpdate)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default SaleDetails;
